import React, { useEffect, useState } from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Tab,
  Tabs,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { makeStyles } from '@mui/styles'
import { renderToString } from 'react-dom/server'

import TextInputComponent, { TextFieldType } from '../../../shared/components/TextInputComponent'
import { EventObject } from '../../../screens/App/Events/EventComponent'
import DropDownComponent, { DropDownElement } from '../../../shared/components/DropDownComponent'
import getEventLocationDropdownOptions from '../../../dataMapping/getEventLocationDropdownOptions'
import {
  Entity,
  EventLocation,
  EventLocationResponse,
  EventPriceCategory,
  EventPriceCategoryResponse,
  EventResponse,
  FetchResponse,
  Gender,
  Member,
  MemberResponse,
  Method,
  Severity,
  User,
} from '../../../shared/types'
import { useDispatch, useSelector } from 'react-redux'
import { getEventLocations, getEventPriceCategories, getEvents, getMembers } from '../../../store/data/data.selectors'
import DatePickerComponent from '../../../shared/components/DatePickerComponent'
import getCustomDateFromDate from '../../../util/getCustomDateFromDate'
import getDateFromCustomDate from '../../../util/getDateFromCustomDate'
import ButtonComponent from '../../../shared/components/ButtonComponent'
import getEventMemberDropdownOptions from '../../../dataMapping/getEventMemberDropdownOptions'
import MediaUploadComponent from '../../../shared/components/MediaUploadComponent'
import LocationSearch from '../../../shared/components/Location/LocationSearch'
import LocationMap from '../../../shared/components/Location/LocationMap'
import { LatLng } from 'leaflet'
import fetchData from '../../../fetch/fetchData'
import { getApiVersion } from '../../../shared/config/getApiVersion'
import Env from '../../../shared/config/envConfig'
import * as dataActions from '../../../store/data/data.actions'
import { setMessage } from '../../../store/settings/settings.actions'
import EuroIcon from '@mui/icons-material/Euro'
import NotesIcon from '@mui/icons-material/Notes'
import AddIcon from '@mui/icons-material/Add'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import EmailIcon from '@mui/icons-material/Email'
import { getActiveMember, getUser } from '../../../store/settings/settings.selectors'

import formatDate from '../../../util/formatDate'
import formatCurrency from '../../../util/formatCurrency'
import CheckBoxComponent from '../../../shared/components/CheckBoxComponent'
import RichTextEditorComponent from '../../../shared/components/RichTextEditorComponent'
import DOMPurify from 'dompurify'
import { addCsrfHeaders } from '../../../shared/config/csrf'

const useStyles = makeStyles((theme: Theme) => ({
  paper: {
    margin: '25px 25px 25px 0',
    padding: '25px',
    width: '100%',
  },
  baseBox: {
    position: 'relative',
  },
}))

interface Props {
  data: EventObject
  setAdminMode: (adminMode: boolean) => void
}

enum TabEnum {
  general = 'general',
  prices = 'prices',
  email = 'email'
}

interface EmailObject {
  subject: string
  template?: string
  text?: string
  senderId: string
  recipients: string[]
  testEmail?: string
}

const defaultEmailObject: EmailObject = {
  subject: '',
  template: undefined,
  text: undefined,
  senderId: '',
  recipients: [],
}

enum EmailTemplate {
  invitation = 'invitation'
}

enum MemberOption {
  member = 'member',
  eventMember = 'eventMember',
  player = 'player',
  custom = 'custom',
}

const TimelineElementEditor: React.FC<Props> = (props) => {
  const classes = useStyles()
  const dispatch = useDispatch()

  const basePath = Env.restBase
  const fetchHeaders = Env.fetchHeaders
  addCsrfHeaders(fetchHeaders)

  const theme: Theme = useTheme()
  const downSm = useMediaQuery(theme.breakpoints.down('sm'))
  const downMd = useMediaQuery(theme.breakpoints.down('md'))
  const downLg = useMediaQuery(theme.breakpoints.down('lg'))

  const activeMember: Member | undefined = useSelector(getActiveMember)
  const eventLocations: EventLocationResponse | undefined = useSelector(getEventLocations)
  const member: MemberResponse | undefined = useSelector(getMembers)
  const user: User | undefined = useSelector(getUser)
  const eventPrices: EventPriceCategoryResponse | undefined = useSelector(getEventPriceCategories)
  const events: EventResponse | undefined = useSelector(getEvents)

  const [data, setData] = useState<EventObject>(props.data)
  const [emailData, setEmailData] = useState<EmailObject>(defaultEmailObject)
  const [emailTemplateHtml, setEmailTemplateHtml] = useState<JSX.Element | undefined>()
  const [testMemberEmail, setTestMemberEmail] = useState<string | undefined>()
  const [memberOption, setMemberOption] = useState<MemberOption | undefined>()

  const [eventLocationOptions, setEventLocationOptions] = useState<DropDownElement[]>([])
  const [eventMemberOptions, setEventMemberOptions] = useState<DropDownElement[]>([])
  const [showNewLocationDialog, setShowNewLocationDialog] = useState<boolean>(false)
  const [eventImages, setEventImages] = useState<string[] | undefined>(undefined)
  const [position, setPosition] = useState<LatLng>(new LatLng(51.0834196, 10.4234469))

  const [activeTab, setActiveTab] = React.useState<TabEnum>(TabEnum.general)

  const [newLocation, setNewLocation] = useState<EventLocation>(
    {
      name: '',
    })

  const setEventPriceCategories = (fetchData: FetchResponse) => {
    setData({
      ...data,
      eventPrices: fetchData.results,
    })

    const newArray: EventPriceCategory[] = []
    eventPrices?.results.map(elem => {
      if (elem.eventId !== data.event.id) {
        newArray.push(elem)
      }
    })
    fetchData.results.map(elem => {
      newArray.push(elem)
    })
    const newEventPrices = {
      count: newArray.length,
      results: newArray,
    }
    dispatch(dataActions.setEventPriceCategories(newEventPrices))
  }

  const setEventLocations = (data: FetchResponse) => {
    dispatch(dataActions.setEventLocations({ count: data.count, results: data.results }))
  }

  useEffect(() => {
    const opt = eventLocations ? getEventLocationDropdownOptions(eventLocations) : []
    opt.unshift({
      key: 'new',
      value: '<em>Neuen Veranstaltungsort anlegen</em>',
    },
    )
    setEventLocationOptions(opt)
  }, [eventLocations])

  useEffect(() => {
    const opt = member ? getEventMemberDropdownOptions(member) : []
    setEventMemberOptions(opt)
  }, [member])

  useEffect(() => {
    setData(props.data)
    setEventImages(props.data.event.imageUrls || [])
    setActiveTab(TabEnum.general)
    setEmailTemplateHtml(undefined)
    setEmailData(defaultEmailObject)
  }, [props.data, props.data.eventPrices])

  const setEvents = (data: FetchResponse) => {
    const results = data.results.sort((a, b) => {
      const yearA = a.eventFrom.year
      const yearB = b.eventFrom.year
      if (yearA > yearB) {
        return -1
      }
      if (yearA < yearB) {
        return 1
      }
      return 0
    })
    dispatch(dataActions.setEvents({ count: data.count, results: results }))
  }

  const [expanded, setExpanded] = React.useState<string | undefined>()
  const [temporaryPricesEventId, setTemporaryPricesEventId] = React.useState<string | undefined>()

  const getOlderPriceOptions = () => {
    const olderPriceOptions: DropDownElement[] = []
    eventPrices?.results.map(price => {
      const element = {
        key: price.eventId,
        value: events?.results.find(elem => elem.id === price.eventId)?.title || price.eventId,
      }
      if (!olderPriceOptions.find(old => old.key === price.eventId)) {
        olderPriceOptions.push(element)
      }
    })

    return olderPriceOptions
  }

  const getInvitation = (): JSX.Element => {
    return (
      <>
        <p>Hallo [vorname],</p>
        <p>
          ich hoffe, diese Nachricht erreicht dich bei bester Gesundheit und noch besserer Laune. Es ist mal wieder an
          der Zeit, dass wir uns alle zum gemütlichen Familientreffen zusammenfinden.<br /><br />

          Deshalb möchte ich euch herzlich zu unserem nächsten Familientreffen einladen, das
          vom {formatDate(props.data.event.eventFrom)} bis
          zum {formatDate(props.data.event.eventTo)} in {props.data.eventLocation?.name} stattfinden wird. Wir haben
          wieder eine wundervolle Herberge ausgewählt, die genug Platz für uns alle bietet und uns die Möglichkeit gibt,
          gemeinsam zu feiern, zu lachen und schöne Erinnerungen zu schaffen.<br /><br />

          <b>Veranstaltungsdetails</b><br />

          Datum: {formatDate(props.data.event.eventFrom)} bis {formatDate(props.data.event.eventTo)}<br />
          Ort: {props.data.eventLocation?.name}<br />
          {props.data.eventLocation?.coordinates &&
            <>
              Anreise: <a
                href={`https://www.google.de/maps/dir//'${props.data.eventLocation?.coordinates}'/@${props.data.eventLocation?.coordinates},13z/data=${props.data.eventLocation?.coordinates}?entry=ttu`}
                target='_blank'
              >Google Maps
              </a>
            </>}
          <br /><br />
        </p>

        {props.data.eventLocation?.description &&
          <p>
            <b>Informationen zur Unterkunft</b><br />
            <div
              dangerouslySetInnerHTML={{ __html: `${DOMPurify.sanitize(props.data.eventLocation.description)}` }}
            />
            <br /><br />
          </p>}

        {data.eventPrices.length > 0 &&
          <p>
            <b>Preisinformationen</b><br />
            <table>
              {data.eventPrices?.sort((a, b) => {
                const nameA = a.title.toUpperCase()
                const nameB = b.title.toUpperCase()
                if (nameA < nameB) {
                  return -1
                }
                if (nameA > nameB) {
                  return 1
                }
                return 0
              }).map((priceElement, index) => {
                return (
                  <tr key={index}>
                    <td>
                      {priceElement.title}
                    </td>
                    <td>
                      {priceElement.description}
                    </td>
                    <td align='right'>
                      {formatCurrency(priceElement.price || 0)}
                    </td>
                  </tr>
                )
              })}
            </table>
          </p>}

        <p>
          Bitte meldet euch verbindlich bis spätestens {formatDate(props.data.event.registrationUntil)} an. Das geht
          ganz einfach online unter
          <a href='https://www.familienskatturnier.de' target='_blank'> www.familienskatturnier.de</a>. Dort werden
          alle eure Buchungen und auch bereits hinterlegte Allergien / Unverträglichkeiten
          berücksichtigt.

          {(activeMember?.email || activeMember?.phone) &&
            <><br />Alternativ könnt ihr euch auch, wie bisher </>}
          {activeMember?.email &&
            <> per E-Mail an {activeMember?.email}</>}
          {activeMember?.phone &&
            <> oder per SMS/WhatsApp an {activeMember?.phone}</>} anmelden. Bitte gebt dann an, wie viele Erwachsene und
          Kinder in welchem Alter teilnehmen werden. Zusätzlich
          bitte ich euch, besondere Ernährungsformen, eventuelle Allergien oder Unverträglichkeiten anzugeben, damit wir
          entsprechende Vorkehrungen treffen können.<br /><br />

          Wir freuen uns auf jeden einzelnen von euch, entspannte Tage im Kreis der Familie und spannende Skatrunden.
        </p>
        <p>
          Liebe Grüße, <br />
          {activeMember?.gender === Gender.female ? 'Eure ' : (activeMember?.gender === Gender.male ? 'Euer ' : '')}{activeMember?.firstname}
        </p>
      </>
    )
  }

  return (
    <Paper className={classes.paper}>
      <Tabs
        sx={{ marginBottom: '20px' }}
        value={activeTab}
        onChange={(e, newValue) => setActiveTab(newValue)}
      >
        <Tab
          sx={{ minWidth: '70px!important' }}
          key={TabEnum.general}
          value={TabEnum.general}
          label={downMd ? undefined : 'Allgemein'}
          icon={downMd ? <NotesIcon /> : undefined}
        />
        <Tab
          sx={{ minWidth: '70px!important' }}
          key={TabEnum.prices}
          value={TabEnum.prices}
          icon={downMd ? <EuroIcon /> : undefined}
          label={downMd ? undefined : 'Preisliste'}
        />
        <Tab
          sx={{ minWidth: '70px!important' }}
          key={TabEnum.email}
          value={TabEnum.email}
          icon={downMd ? <EmailIcon /> : undefined}
          label={downMd ? undefined : 'E-Mail'}
        />
      </Tabs>

      {/* Bereich Email */}
      {activeTab === TabEnum.email &&
        <>
          <Box>
            <DropDownComponent
              label='Vorlage auswählen'
              options={[
                {
                  key: EmailTemplate.invitation,
                  value: 'Einladung',
                },
              ]}
              onChange={(value) => {
                let templateString = ''
                switch (value?.key) {
                  case EmailTemplate.invitation:
                    // @ts-ignore
                    templateString = renderToString(getInvitation()).replace('[vorname]', '%s')
                    setEmailTemplateHtml(getInvitation())
                }

                setEmailData({
                  ...emailData,
                  subject: `Einladung zum "Otto-Meißner-Pokal" Familientreffen ${props.data.event.eventFrom?.year}`,
                  template: templateString,
                  text: undefined,
                })
              }}
            />
          </Box>
          <Divider sx={{ margin: '20px 0' }} />
          <TextInputComponent
            label='Betreff'
            fullWidth
            required
            value={emailData.subject}
            onChange={(value?: string | number) => {
              setEmailData({
                ...emailData,
                subject: value as string || '',
              })
            }}
          />
          {emailTemplateHtml &&
            <>
              {emailTemplateHtml}
            </>}
          {!emailTemplateHtml &&
            <RichTextEditorComponent
              onChange={(value?: string) => {
                setEmailData({
                  ...emailData,
                  template: undefined,
                  text: value,
                })
                setEmailTemplateHtml(undefined)
              }}
            />}
          <Divider sx={{ margin: '20px 0' }} />
          <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
            <Box sx={{ width: '100%' }}>
              <CheckBoxComponent
                value={memberOption === MemberOption.member}
                label='Alle Familienmitglieder auswählen'
                onChange={(checked) => {
                  setMemberOption(MemberOption.member)
                  if (checked) {
                    const newRecipients: string[] = []
                    {
                      member?.results.map(tempMember => {
                        if (tempMember.email) {
                          newRecipients.push(tempMember.originId)
                        }
                      })
                    }
                    setEmailData({
                      ...emailData,
                      recipients: newRecipients,
                    })
                  }
                }}
              />
            </Box>
            <Box sx={{ width: '100%' }}>
              <CheckBoxComponent
                value={memberOption === MemberOption.eventMember}
                label='Nur angemeldete Familienmitglieder auswählen'
                onChange={(checked) => {
                  setMemberOption(MemberOption.eventMember)
                  if (checked) {
                    const newRecipients: string[] = []
                    {
                      props.data.eventMember.map(eventMember => {
                        newRecipients.push(eventMember.memberId)
                      })
                    }
                    setEmailData({
                      ...emailData,
                      recipients: newRecipients,
                    })
                  }
                }}
              />
            </Box>
            <Box sx={{ width: '100%' }}>
              <CheckBoxComponent
                value={memberOption === MemberOption.player}
                label='Alle Skatspieler auswählen'
                onChange={(checked) => {
                  setMemberOption(MemberOption.player)
                  if (checked) {
                    const newRecipients: string[] = []
                    {
                      props.data.eventMember.map(eventMember => {
                        if (eventMember.gamer) {
                          newRecipients.push(eventMember.memberId)
                        }
                      })
                    }
                    setEmailData({
                      ...emailData,
                      recipients: newRecipients,
                    })
                  }
                }}
              />
            </Box>
            <Box sx={{ width: '100%' }}>
              <CheckBoxComponent
                value={memberOption === MemberOption.custom}
                label='Nur bestimmte Familienmitglieder auswählen'
                onChange={(checked) => {
                  setMemberOption(MemberOption.custom)
                  setEmailData({
                    ...emailData,
                    recipients: [],
                  })
                }}
              />

              <Divider sx={{ margin: '20px 0' }} />

              {memberOption === MemberOption.custom &&
                <Box>
                  {member?.results.sort((a, b) => {
                    const nameA = a.firstname.toUpperCase()
                    const nameB = b.firstname.toUpperCase()
                    if (nameA < nameB) {
                      return -1
                    }
                    if (nameA > nameB) {
                      return 1
                    }
                    return 0
                  }).map(tempMember => {
                    if (tempMember.email) {
                      return (
                        <Box sx={{ width: '100%' }}>
                          <CheckBoxComponent
                            value={(emailData.recipients.findIndex(elem => elem === tempMember.originId) > -1)}
                            label={`${tempMember.firstname} ${tempMember.lastname}`}
                            onChange={(checked) => {
                              const newRecipients = emailData.recipients
                              if (checked) {
                                newRecipients.push(tempMember.originId)
                              } else {
                                newRecipients.splice(newRecipients.findIndex(elem => elem === tempMember.originId), 1)
                              }
                              setEmailData({
                                ...emailData,
                                recipients: newRecipients,
                              })
                            }}
                          />
                        </Box>
                      )
                    }
                  })}
                </Box>}
            </Box>
          </Box>
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            margin: '20px 0',
            flexWrap: downSm ? 'wrap' : 'nowrap',
          }}
          >
            <Box sx={{
              width: downSm ? '100%' : '45%',
              display: 'flex',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              marginRight: downSm ? 0 : '10px',
            }}
            >
              <Box sx={{ width: '100%' }}>
                <TextInputComponent
                  fullWidth
                  value={testMemberEmail}
                  size='small'
                  label='Test E-Mail'
                  onChange={(value?: string | number) => {
                    setTestMemberEmail(value as string)
                  }}
                />
              </Box>
              <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', marginTop: downSm ? '10px' : 0 }}>
                <ButtonComponent
                  fullWidth
                  text='Test-E-Mail absenden'
                  disabled={emailData.subject === '' || (!emailData.template && !emailData.text) || !testMemberEmail}
                  onClick={() => {
                    if (emailData.subject !== '' && emailData.template !== '') {
                      const restUrl = `${basePath}/api/email/send`
                      const body = {
                        ...emailData,
                        senderId: user?.memberId,
                        recipients: [],
                        testEmail: testMemberEmail,
                      }
                      setTestMemberEmail(undefined)
                      fetch(restUrl,
                        {
                          method: 'POST',
                          credentials: 'include',
                          headers: fetchHeaders,
                          body: JSON.stringify(body),
                        })
                        .then(res => res.json())
                        .then(json => {
                          setMessage({ message: 'E-Mail erfolgreich versandt', severity: Severity.success })
                        })
                        .catch(err => console.log(err))
                    }
                  }}
                />
              </Box>
            </Box>
            <Box sx={{
              width: downSm ? '100%' : '45%',
              display: 'flex',
              alignItems: 'flex-end',
              marginTop: downSm ? '10px' : 0,
            }}
            >
              <ButtonComponent
                fullWidth
                size='medium'
                text='E-Mail(s) absenden'
                disabled={emailData.subject === '' || (!emailData.template && !emailData.text)}
                onClick={() => {
                  if (emailData.subject !== '' && emailData.template !== '') {
                    const restUrl = `${basePath}/api/email/send`
                    const body = {
                      ...emailData,
                      senderId: user?.memberId,
                      testEmail: undefined,
                    }
                    setEmailData(defaultEmailObject)
                    setEmailTemplateHtml(undefined)
                    setMemberOption(undefined)
                    fetch(restUrl,
                      {
                        method: 'POST',
                        credentials: 'include',
                        headers: fetchHeaders,
                        body: JSON.stringify(body),
                      })
                      .then(res => res.json())
                      .then(json => {
                        setMessage({ message: 'E-Mails erfolgreich versandt', severity: Severity.success })
                      })
                      .catch(err => console.log(err))
                  }
                }}
              />
            </Box>
          </Box>
        </>}

      {/* Bereich Preislisten */}
      {activeTab === TabEnum.prices &&
        <>
          {data.eventPrices.length === 0 &&
            <>
              <Typography variant='body1'>Hier können Buchungsoptionen mit Preisen angelegt oder aus einem der
                Vorjahre übernommen werden.
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'nnowrap' }}>
                <Box sx={{ width: '45%' }}>
                  <DropDownComponent
                    fullWidth
                    size='small'
                    options={getOlderPriceOptions()}
                    label='Preislisten der Vorjahre'
                    onChange={(value) => {
                      setTemporaryPricesEventId(value?.key)
                    }}
                  />
                </Box>
                <Box sx={{ width: '45%', paddingTop: '10px' }}>
                  <ButtonComponent
                    fullWidth
                    size='medium'
                    disabled={!temporaryPricesEventId}
                    text='Auswählen'
                    onClick={() => {
                      if (props.data.event.id) {
                        const tempArray: EventPriceCategory[] = []
                        temporaryPricesEventId && eventPrices?.results.map(price => {
                          if (price.eventId === temporaryPricesEventId) {
                            props.data.event.id && tempArray.push({ ...price, eventId: props.data.event.id })
                          }
                        })
                        setData({
                          ...data,
                          eventPrices: tempArray,
                        })
                        tempArray.map(elem => {
                          fetchData(basePath, getApiVersion(), fetchHeaders, Entity.eventPriceCategories, Method.POST, undefined, { priceCategory: elem }, undefined, setMessage)
                        })
                      }
                    }}
                  />
                </Box>
              </Box>
            </>}
          {data.eventPrices?.sort((a, b) => {
            const nameA = a.title.toUpperCase()
            const nameB = b.title.toUpperCase()
            if (nameA < nameB) {
              return -1
            }
            if (nameA > nameB) {
              return 1
            }
            return 0
          }).map((priceElement, index) => {
            return (
              <Accordion
                sx={{ margin: '0px!important', borderBottom: `1px solid ${theme.palette.grey.A400}` }}
                TransitionProps={{ unmountOnExit: true }} expanded={expanded === priceElement.id}
                onChange={() => {
                  expanded === priceElement.id ? setExpanded(undefined) : setExpanded(priceElement.id)
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>{priceElement.title}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid
                    key={index}
                    spacing={theme.spacing(1)}
                    container
                  >
                    <Grid key={index} item xs={12}>
                      <TextInputComponent
                        label='Titel'
                        value={priceElement.title}
                        required
                        fullWidth
                        onChange={(value?: string | number) => {
                          const newValue: string = value as string || ''
                          const tempArray = data.eventPrices.map(elem => {
                            if (elem.id === priceElement.id) {
                              elem.title = newValue
                            }
                            return elem
                          })

                          setData({
                            ...data,
                            eventPrices: tempArray,
                          })
                        }}
                      />
                    </Grid>
                    <Grid key={index} item xs={12}>
                      <TextInputComponent
                        label='Beschreibung'
                        value={priceElement.description}
                        multiline
                        minRows={2}
                        fullWidth
                        onChange={(value?: string | number) => {
                          const newValue: string = value as string || ''
                          const tempArray = data.eventPrices.map(elem => {
                            if (elem.id === priceElement.id) {
                              elem.description = newValue
                            }
                            return elem
                          })

                          setData({
                            ...data,
                            eventPrices: tempArray,
                          })
                        }}
                      />
                    </Grid>
                    <Grid key={index} item xs={6}>
                      <TextInputComponent
                        label='Preis'
                        value={priceElement.price}
                        type={TextFieldType.number}
                        fullWidth
                        required
                        startAdornment={
                          <InputAdornment position='start'>€</InputAdornment>
                        }
                        onChange={(value?: string | number) => {
                          const newValue: number = value as number
                          const tempArray = data.eventPrices.map(elem => {
                            if (elem.id === priceElement.id) {
                              elem.price = newValue
                            }
                            return elem
                          })

                          setData({
                            ...data,
                            eventPrices: tempArray,
                          })
                        }}
                      />
                    </Grid>
                    <Grid key={index} item xs={6}>
                      <TextInputComponent
                        label='Anzahl'
                        value={priceElement.defaultCount}
                        type={TextFieldType.number}
                        fullWidth
                        required
                        onChange={(value?: string | number) => {
                          const newValue: number = value as number
                          const tempArray = data.eventPrices.map(elem => {
                            if (elem.id === priceElement.id) {
                              elem.defaultCount = newValue
                            }
                            return elem
                          })

                          setData({
                            ...data,
                            eventPrices: tempArray,
                          })
                        }}
                      />
                    </Grid>
                    <Grid key={index} item xs={6}>
                      <TextInputComponent
                        label='Alter von'
                        value={priceElement.ageFrom}
                        type={TextFieldType.number}
                        fullWidth
                        onChange={(value?: string | number) => {
                          const newValue: number = value as number
                          const tempArray = data.eventPrices.map(elem => {
                            if (elem.id === priceElement.id) {
                              elem.ageFrom = newValue
                            }
                            return elem
                          })

                          setData({
                            ...data,
                            eventPrices: tempArray,
                          })
                        }}
                      />
                    </Grid>
                    <Grid key={index} item xs={6}>
                      <TextInputComponent
                        label='Alter bis'
                        value={priceElement.ageTo}
                        type={TextFieldType.number}
                        fullWidth
                        onChange={(value?: string | number) => {
                          const newValue: number = value as number
                          const tempArray = data.eventPrices.map(elem => {
                            if (elem.id === priceElement.id) {
                              elem.ageTo = newValue
                            }
                            return elem
                          })

                          setData({
                            ...data,
                            eventPrices: tempArray,
                          })
                        }}
                      />
                    </Grid>
                  </Grid>
                  <Box
                    sx={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'space-between',
                    }}
                  >
                    <ButtonComponent
                      text='Löschen'
                      color='secondary'
                      onClick={() => {
                        if (priceElement.id === 'new') {
                          const dataPrices: EventPriceCategory[] = []
                          data.eventPrices.map(elem => {
                            if (elem.id !== 'new') {
                              dataPrices.push(elem)
                            }
                          })
                          setData({
                            ...data,
                            eventPrices: dataPrices,
                          })
                        } else {
                          fetchData(basePath, getApiVersion(), fetchHeaders, Entity.eventPriceCategories, Method.DELETE, priceElement.id, undefined, setEventPriceCategories, setMessage)
                        }
                        setExpanded(undefined)
                      }}
                    />
                    <ButtonComponent
                      text='Speichern'
                      onClick={() => {
                        if (priceElement.id === 'new') {
                          fetchData(basePath, getApiVersion(), fetchHeaders, Entity.eventPriceCategories, Method.POST, undefined, { priceCategory: data.eventPrices.find(elem => elem.id === 'new') }, setEventPriceCategories, setMessage)
                        } else {
                          fetchData(basePath, getApiVersion(), fetchHeaders, Entity.eventPriceCategories, Method.PUT, priceElement.id, { priceCategory: data.eventPrices.find(elem => elem.id === priceElement.id) }, setEventPriceCategories, setMessage)
                        }
                        setExpanded(undefined)
                      }}
                    />
                  </Box>
                </AccordionDetails>
              </Accordion>)
          })}
          <IconButton
            disabled={!!data.eventPrices.find(elem => elem.id === 'new')}
            onClick={() => {
              if (data.event.id) {
                const newPriceElement: EventPriceCategory = {
                  id: 'new',
                  eventId: data.event.id,
                  defaultCount: 1,
                  title: '',
                  description: '',
                  price: 0.00,
                }
                const dataPrices = data.eventPrices
                if (!dataPrices.find(elem => elem.id === 'new')) {
                  dataPrices.push(newPriceElement)
                  setData({
                    ...data,
                    eventPrices: dataPrices,
                  })
                  setExpanded('new')
                }
              }
            }}
          >
            <AddIcon />
          </IconButton>
        </>}
      {/* Bereich Allgemeine Daten */}
      {activeTab === TabEnum.general &&
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextInputComponent
              label='Titel'
              value={data?.event.title}
              required
              fullWidth
              onChange={(value?: string | number) => {
                const newValue: string = value as string || ''
                setData({
                  ...data,
                  event: {
                    ...data.event,
                    title: newValue,
                  },
                })
              }}
            />
          </Grid>
          <Grid container item xs={downLg ? 12 : (downSm ? 12 : 6)} spacing={2}>
            <Grid item xs={6}>
              <DatePickerComponent
                label='Von'
                fullWidth
                value={data.event.eventFrom && getDateFromCustomDate(data.event.eventFrom)}
                onChangeDate={(value) => {
                  setData({
                    ...data,
                    event: {
                      ...data.event,
                      eventFrom: value ? getCustomDateFromDate(value) : {},
                    },
                  })
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <DatePickerComponent
                label='Bis'
                fullWidth
                minDate={data.event.eventFrom && getDateFromCustomDate(data.event.eventFrom)}
                value={data.event.eventTo && getDateFromCustomDate(data.event.eventTo)}
                onChangeDate={(value) => {
                  setData({
                    ...data,
                    event: {
                      ...data.event,
                      eventTo: value ? getCustomDateFromDate(value) : {},
                    },
                  })
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <DropDownComponent
                label='Veranstaltungsort'
                options={eventLocationOptions}
                value={eventLocationOptions.find(elem => elem.key === data.event.locationId)?.key}
                multiple={false}
                onChange={(value) => {
                  if (value?.key === 'new') {
                    setShowNewLocationDialog(true)
                    setData({
                      ...data,
                      event: {
                        ...data.event,
                        locationId: undefined,
                      },
                    })
                  } else {
                    setData({
                      ...data,
                      event: {
                        ...data.event,
                        locationId: value?.key,
                      },
                    })
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextInputComponent
                label='Signatur'
                value={data.event.signature}
                fullWidth
                onChange={(value?: string | number) => {
                  const newValue: string = value as string || ''
                  setData({
                    ...data,
                    event: {
                      ...data.event,
                      signature: newValue,
                    },
                  })
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <DatePickerComponent
                label='Anmeldung bis'
                fullWidth
                value={data.event.registrationUntil && getDateFromCustomDate(data.event.registrationUntil)}
                onChangeDate={(value) => {
                  value && setData({
                    ...data,
                    event: {
                      ...data.event,
                      registrationUntil: getCustomDateFromDate(value),
                    },
                  })
                }}
              />
            </Grid>
          </Grid>
          <Grid container item xs={downLg ? 12 : (downSm ? 12 : 6)} spacing={2}>
            <Grid item xs={12}>
              <RichTextEditorComponent
                label='Zusammenfassung'
                height='330px'
                tooltip='Hier kann die Zusammenfassung des Events eingetragen werden, falls verfügbar.'
                value={data.event.description}
                onChange={(value?: string) => {
                  let newValue: string | undefined = value as string || undefined
                  if (newValue === '' || newValue === ' ') {
                    newValue = undefined
                  }
                  setData({
                    ...data,
                    event: {
                      ...data.event,
                      description: newValue,
                    },
                  })
                }}
              />
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <DropDownComponent
              label='Organisatoren'
              options={eventMemberOptions}
              multipleValue={data.event.organizer}
              multiple
              onChangeList={(value) => {
                const organizer: string[] = []
                value.map(elem => {
                  organizer.push(elem.key)
                })
                setData({
                  ...data,
                  event: {
                    ...data.event,
                    organizer,
                  },
                })
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <MediaUploadComponent
              allowVideoUpload={false}
              file={eventImages}
              setMediaFile={(file) => setEventImages(file)}
            />
          </Grid>
          <Grid item xs={12} textAlign='right'>
            <ButtonComponent
              text={data.event.id === 'new' ? 'Erstellen' : 'Speichern'}
              color='primary'
              onClick={() => {
                if (eventImages) {
                  // TODO neues Bild hochladen, auf id warten und dannn
                }
                let method = Method.PUT
                let id = data.event.id
                if (!data.event.id || data.event.id === 'new') {
                  method = Method.POST
                  delete data.event.id
                  id = undefined
                }
                fetchData(basePath, getApiVersion(), fetchHeaders, Entity.events, method, id, { event: data.event }, setEvents, setMessage)
                props.setAdminMode(false)
              }}
            />
          </Grid>
        </Grid>}
      <Dialog
        open={showNewLocationDialog} onClose={() => setShowNewLocationDialog(false)}
        sx={{ '& .MuiDialog-paper': { borderRadius: 0, minWidth: '80%', maxWidth: '80%' } }}
      >
        <Stack sx={{ p: 3 }}>
          <Stack
            direction='row' spacing={3} alignItems='center'
            justifyContent='space-between' className={classes.baseBox}
          >
            <DialogTitle sx={{ fontWeight: 600, p: 0, fontSize: '16px', marginBottom: '20px' }}>
              Neuer Veranstaltungsort
            </DialogTitle>
          </Stack>
          <DialogContent sx={{ p: 0, overflow: 'visible' }}>
            <Grid container spacing={1}>
              <Grid item xs={downMd ? 12 : 6}>
                <TextInputComponent
                  label='Name'
                  required
                  fullWidth
                  onChange={(value?: string | number) => {
                    const newValue: string = value as string || ''
                    newLocation && setNewLocation({
                      ...newLocation,
                      name: newValue,
                    })
                  }}
                />
              </Grid>
              <Grid item xs={downMd ? 12 : 6}>
                <TextInputComponent
                  label='Signatur'
                  required
                  fullWidth
                  onChange={(value?: string | number) => {
                    const newValue: string = value as string || ''
                    newLocation && setNewLocation({
                      ...newLocation,
                      signature: newValue,
                    })
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextInputComponent
                  label='Beschreibung'
                  required
                  multiline
                  fullWidth
                  minRows={5}
                  onChange={(value?: string | number) => {
                    const newValue: string = value as string || ''
                    newLocation && setNewLocation({
                      ...newLocation,
                      description: newValue,
                    })
                  }}
                />
              </Grid>
              <Grid item xs={downMd ? 12 : 6}>
                <TextInputComponent
                  label='Linktitel'
                  fullWidth
                  onChange={(value?: string | number) => {
                    const newValue: string = value as string || ''
                    newLocation && setNewLocation({
                      ...newLocation,
                      caption: newValue,
                    })
                  }}
                />
              </Grid>
              <Grid item xs={downMd ? 12 : 6}>
                <TextInputComponent
                  label='Linkurl'
                  fullWidth
                  onChange={(value?: string | number) => {
                    const newValue: string = value as string || ''
                    newLocation && setNewLocation({
                      ...newLocation,
                      linkUrl: newValue,
                    })
                  }}
                />
              </Grid>
              <Grid item xs={downMd ? 12 : 6}>
                <LocationSearch
                  destination={newLocation?.name}
                  setPosition={(obj) => {
                    setPosition(new LatLng(obj.lat, obj.long))
                    newLocation && setNewLocation({
                      ...newLocation,
                      coordinates: `${obj.lat},${obj.long}`,
                    })
                  }}
                />
                <LocationMap mapZoom={12} positions={[position]} />
              </Grid>
              <Grid item xs={downMd ? 12 : 6}>
                <MediaUploadComponent
                  allowVideoUpload={false}
                  file={undefined}
                  setMediaFile={(file) =>
                    // TODO save file
                    file && newLocation && setNewLocation({
                      ...newLocation,
                      imageUrls: [file],
                    })}
                />
              </Grid>
            </Grid>
          </DialogContent>
        </Stack>
        <DialogActions sx={{ p: 3 }}>
          <ButtonComponent
            color='primary'
            variant='text'
            text='Verwerfen'
            onClick={() => {
              setNewLocation({
                name: '',
                imageUrls: [],
              })
              setShowNewLocationDialog(false)
            }}
          />
          <ButtonComponent
            color='primary'
            variant='contained'
            text='Speichern'
            onClick={() => {
              fetchData(basePath, getApiVersion(), fetchHeaders, Entity.eventLocations, Method.POST, undefined, { location: newLocation }, setEventLocations, setMessage)
              setShowNewLocationDialog(false)
            }}
          />
        </DialogActions>
      </Dialog>
    </Paper>
  )
}

export default TimelineElementEditor
